(ns practitest.frontend.subs
  (:require
    [re-frame.core :as re-frame]
    [clojure.data :refer [diff]]
    [clojure.set :refer [rename-keys]]
    [practitest.frontend.components.forms :as forms]))

(def index-grid-keys {:requirement :requirements-grid
                      :test        :tests-grid
                      :testset     :testsets-grid
                      :issue       :issues-grid
                      :testsets-instances :testsets-instances-grid})

(re-frame/reg-sub
  ::options
  (fn [db _]
    (:options db)))

(re-frame/reg-sub
 ::system-messages
 :<- [::options]
 (fn [{:keys [system-messages]} _]
   system-messages))

(re-frame/reg-sub
  ::status-colors
  (fn [_]
    (re-frame/subscribe [::options]))
  (fn [{:keys [status-colors]} _]
    (into {} status-colors)))

(re-frame/reg-sub
  ::entity-fields
  (fn [db [_ entity]]
    (get-in db [:entity-fields entity])))

(re-frame/reg-sub
  ::entity-sections
  (fn [db [_ entity]]
    (get-in db [:entity-sections entity])))

(re-frame/reg-sub
  ::online-users
  (fn [db _]
    (:online-users db)))

(re-frame/reg-sub
 ::all-entity-sections
 (fn [db [_]]
   (:entity-sections db)))

(re-frame/reg-sub
  ::all-entity-fields
  (fn [db _]
    (:entity-fields db)))

(def entity-types [:test :issue :requirement :testset])

(def ^:private system-fields-by-entity
  "Fields stored in :fields but not assigned to any entity via :entity-fields."
  {:test #{:name}
   :testset #{:name}
   :issue #{:title}
   :requirement #{:name}})

(def ^:private synthetic-fields-by-entity
  "Fields not in :fields at all but supported by PTQL - inject as synthetic descriptors."
  {:test [{:id "display_id" :name "ID" :type "number"}]
   :testset [{:id "display_id" :name "ID" :type "number"}]
   :issue [{:id "display_id" :name "ID" :type "text"}
           {:id "external_id" :name "External ID" :type "text"}]
   :requirement [{:id "display_id" :name "ID" :type "text"}
                 {:id "external_id" :name "External ID" :type "text"}]})

(def entity-kw->ptql-name
  {:test "Test" :testset "TestSet" :issue "Issue" :requirement "Requirement"})

(defn entity-fields-grouped
  "Returns {entity-kw [{:name :type :values}, ...], ...} for the four PTQL entity types.
  Combines :entity-fields with :fields metadata, adds system fields (name/title)
  and synthetic ID descriptors."
  [fields-meta entity-fields-by-kw]
  (into {}
        (map (fn [entity]
               [entity
                (vec (concat (keep (fn [field-key]
                                     (when-let [meta (get fields-meta field-key)]
                                       {:id (if (= :tags field-key) "__TAG__" (name field-key))
                                        :name (or (:label meta) (name field-key))
                                        :type (some-> (:type meta) (name))
                                        :values (when (and (seq (:possible-values meta))
                                                           (not= "user" (:type meta)))
                                                  (vec (:possible-values meta)))}))
                                   (distinct (concat (keys (get entity-fields-by-kw entity))
                                                     (get system-fields-by-entity entity #{}))))
                             (get synthetic-fields-by-entity entity [])))])
             entity-types)))

(defn fields-by-ptql-entity [all-fields entity-fields]
  (->> (entity-fields-grouped all-fields entity-fields)
       (reduce-kv (fn [m k v]
                    (if-let [ptql-name (entity-kw->ptql-name k)]
                      (assoc m ptql-name v)
                      m))
                  {})))

(re-frame/reg-sub
  ::fields-by-ptql-entity
  :<- [::fields]
  :<- [::all-entity-fields]
  (fn [[fields entity-fields-by-kw] _]
    (fields-by-ptql-entity fields entity-fields-by-kw)))

(re-frame/reg-sub
  ::integrated-fields
  (fn [db _]
    (:integrated-fields db)))

(re-frame/reg-sub
  ::fields
  (fn [db _]
    (:fields db)))

(re-frame/reg-sub
  ::settings-fields
  (fn [db _]
    (:settings-fields db)))

(re-frame/reg-sub
  ::users
  (fn [db _]
    (:users db)))

;; Users referenced by entity payloads but not in the project's :users list
;; (typically users removed from the project/account). Keyed by id. Populated
;; by handlers that merge :referenced_users from server responses. Used by
;; find-user as a fallback so attribution still renders.
(re-frame/reg-sub
  ::missing-users
  (fn [db _]
    (:missing-users db)))

(re-frame/reg-sub
  ::groups
  (fn [db _]
    (:groups db)))

(defn get-current-user
  [db]
  (:current-user db))

(re-frame/reg-sub
  ::current-user
  (fn [db _]
    (get-current-user db)))

(re-frame/reg-sub
  ::current-user-timezone
  (fn [db _]
    (get-in db [:current-user :time_zone])))

;; TODO: https://github.com/den1k/re-frame-utils/blob/master/src/vimsical/re_frame/cofx/inject.cljc ??
(defn get-current-project-id
  [db]
  (get-in db [:current-project :id]))

(defn get-current-account-id
  [db]
  (get-in db [:current-account :id]))

(defn get-current-integration-id
  [db]
  (get-in db [:current-project :integrated_bugtracker_id]))

(re-frame/reg-sub
 ::current-project-id
 (fn [db _]
   (get-current-project-id db)))

(defn get-current-project
  [db]
  (:current-project db))

(re-frame/reg-sub
  ::current-project
  (fn [db _]
    (get-current-project db)))

(re-frame/reg-sub
  ::enabled-automation-types
  (fn [db _]
    (set (get-in db [:current-project :automation_types]))))

(re-frame/reg-sub
  ::user-projects
  (fn [db]
    (:user-projects db)))

(re-frame/reg-sub
  ::user-accounts
  (fn [db]
    (:user-accounts db)))

(re-frame/reg-sub
  ::current-account
  (fn [db]
    (:current-account db)))

(re-frame/reg-sub
  ::account-license
  (fn [db _]
    (get-in db [:current-account :license])))

(re-frame/reg-sub
  ::pt-datacenter-account
  (fn [db _]
      (get-in db [:options :pt-datacenter])))

(re-frame/reg-sub
  ::tags
  (fn [db _]
    (:tags db)))

(re-frame/reg-sub
  ::entity
  (fn [db [_ entity]]
    (get-in db [:entity entity])))

(def label-prefix
  {:run     "Run"
   :lastrun "Last Run"
   :test    "Test"
   :testset "TestSet"})

(defn namespaced-label [entity label]
  (str (get label-prefix entity)
       ": "
       label))

(defn parse-namespaced-field [namespaced-field]
  [(-> namespaced-field namespace keyword)
   (-> namespaced-field name keyword)])

(defn get-form-state
  [db form-id]
  (get-in db [:form-states form-id]))

(re-frame/reg-sub
  ::form-state
  (fn [db [_ form-id]]
    (get-form-state db form-id)))

(defn get-field-state
  [form-state field-id]
  (get-in form-state [:fields field-id]))

(re-frame/reg-sub
  ::field-state
  (fn [[_ form-id _]]
    (re-frame/subscribe [::form-state form-id]))
  (fn [form-state [_ _ field-id]]
    (get-field-state form-state field-id)))

;; TODO: improve this ?
(defn field-props [[fields entity-fields users tags lanes groups field-state folders] [_ entity namespaced-field]]
  (let [[field-ns field] (parse-namespaced-field namespaced-field)
        ;; TODO: fix "special" field, e.g. ":name" - it's not in the fields map
        props (get fields field)
        entity-props (get entity-fields field)
        ;; TODO: fix this
        mandatory? (or (= namespaced-field :name)
                       (:mandatory? entity-props))
        field-type (:type props)]
    (merge
      (cond-> props
              field-ns (update :label (partial namespaced-label field-ns))
              (= :user field-type) (assoc :possible-values
                                             ;; TODO: fix this somehow
                                             (if (= :assigned_to field)
                                               ;; "Assigned to" supports both users and groups
                                               (vec (concat users groups))
                                               ;; Other (custom) user fields support only users
                                               users))
              (= :folder_id field-type) (assoc :possible-values folders
                                               :folder-type (case entity
                                                              :test
                                                              "QtestsFolder"
                                                              :testset
                                                              "SuitesFolder"
                                                              ;;default
                                                              "Unknonwn"))
              (= :kanban_lane field-type) (assoc :possible-values lanes)
              (= :memo field-type) (assoc :users users)
              (= :tags field-type) (assoc :possible-values tags))

      entity-props
      (cond-> {:field-state field-state
               :field-id    namespaced-field
               :field       namespaced-field}
              mandatory? (assoc :validators (case field-type
                                              ;; TODO: more field types
                                              (:tags :checkbox :date :date-time :folder_id)
                                              [::forms/value-present]

                                              :user
                                              [::forms/user-selected]

                                              ;; TODO: special validators for e.g. duration
                                              ;else
                                              [::forms/string-not-blank]))))))

(re-frame/reg-sub
  ::field-props
  (fn [[_ entity field-id]]
    [(re-frame/subscribe [::fields])
     (re-frame/subscribe [::entity-fields entity])
     (re-frame/subscribe [::users])
     (re-frame/subscribe [::tags])
     (re-frame/subscribe [:practitest.frontend.kanban/lanes])
     (re-frame/subscribe [::groups])
     (re-frame/subscribe [::field-state entity field-id])
     (re-frame/subscribe [:practitest.frontend.folders/folders])])
  field-props)

;; TODO: not really scalable
(defn get-current-entity-type
  [db]
  (:current-entity db))

(defn get-current-entity-id
  [db entity-type]
  (get-in db [:entity entity-type :current :id]))

(re-frame/reg-sub
  ::current-entity-id
  (fn [db [_ entity]]
    (get-current-entity-id db entity)))

;; TODO: fix this
(re-frame/reg-event-db
  ::set-current-entity-id
  (fn [db [_ entity id]]
    (assoc-in db [:entity entity :current :id] id)))

(re-frame/reg-sub
  ::current-entity-key
  (fn [db [_]]
    (keyword (get-in db [:options :entity-key]))))

(re-frame/reg-sub
  ::current-entity
  (fn [db [_]]
    (:current-entity db)))

(re-frame/reg-sub
  ::progress?
  (fn [db [_ event]]
    (contains? (:progress db) event)))

(re-frame/reg-sub
 ::collab-connected?
 (fn [db _]
   (= :connected (get-in db [:collab :state]))))

(re-frame/reg-sub
 ::remote-changes
 (fn [[_ entity]]
   [(re-frame/subscribe [::entity entity])
    (re-frame/subscribe [::fields])])
 (fn [[{:keys [remote current state changed-by]} fields] _]
   (when-not (empty? remote)
     (let [blacklist [:updated_at]
           [a b _]   (diff (apply dissoc remote blacklist)
                           (apply dissoc current blacklist))
           mapping   (->> fields
                          (map (fn [[k {:keys [label]}]]
                                 [k label]))
                          (into {}))]
       (when (or (not-empty a)
                 (not-empty b))
         {:remote {:raw        a
                   :translated (rename-keys a mapping)}
          :local  {:raw        b
                   :translated (rename-keys b mapping)}
          :changed-by changed-by})))))

(re-frame/reg-sub
 ::current-locale
 :<- [::options]
 (fn [{:keys [current-locale]} _]
   current-locale))

;; TODO: ::field subscription ?
(defn get-field-by-id [id]
  (let [fields @(re-frame/subscribe [::fields])]
    (get fields (keyword id))))

(re-frame/reg-sub
  ::use-test-workflow?
  :<- [::options]
  (fn [{:keys [use-test-workflows?]} _]
      (boolean use-test-workflows?)))

(re-frame/reg-sub
  ::generative-ai-consent?
  :<- [::options]
  (fn [{:keys [generative-ai-consent?]}]
    (boolean generative-ai-consent?)))

(re-frame/reg-sub
  ::project-assistant-enabled?
  :<- [::options]
  (fn [{:keys [project-assistant-enabled?]}]
    (boolean project-assistant-enabled?)))

(re-frame/reg-sub
  ::usage-and-limits-enabled?
  :<- [::options]
  (fn [{:keys [usage-and-limits-enabled?]}]
    (boolean usage-and-limits-enabled?)))

;; Backend CommonBatchController::BATCH_LIMIT, delivered via frontend_config.
;; Falls back to 2000 only if the config key is absent.
(re-frame/reg-sub
  ::batch-limit
  :<- [::options]
  (fn [{:keys [batch-limit]}]
    (or batch-limit 2000)))

(re-frame/reg-sub
  ::new-view-editor-enabled?
  :<- [::options]
  (fn [{:keys [new-view-editor-enabled?]}]
    (boolean new-view-editor-enabled?)))

(re-frame/reg-sub
 ::ptql-debug-bar-enabled?
 :<- [::options]
 (fn [{:keys [ptql-debug-bar-enabled?]}]
   (boolean ptql-debug-bar-enabled?)))

(re-frame/reg-sub
 ::kanban-enabled?
 :<- [::options]
 (fn [{:keys [kanban-enabled?]}]
   (boolean kanban-enabled?)))

(re-frame/reg-sub
  ::test-cloning-update-enabled?
  :<- [::options]
  (fn [{:keys [test-cloning-update-enabled?]}]
    (boolean test-cloning-update-enabled?)))

(re-frame/reg-sub
  ::edit-global-field-values?
  :<- [::options]
  (fn [{:keys [edit-global-field-values?]}]
    (boolean edit-global-field-values?)))

(re-frame/reg-sub
  ::convert-global-field-type-enabled?
  :<- [::options]
  (fn [{:keys [convert-global-field-type-enabled?]}]
    (boolean convert-global-field-type-enabled?)))

(re-frame/reg-sub
  ::new-run-form-enabled?
  :<- [::options]
  (fn [{:keys [new-run-form?]}]
    (boolean new-run-form?)))

(re-frame/reg-sub
 ::get-field-in-form-state2
(fn [db [_ form-id field]]
  (get-in db [:form-states2 form-id :fields field])))

(re-frame/reg-sub
 ::global-fields-supported?
 :<- [::options]
 (fn [{:keys [global-fields-supported?]}]
   (boolean global-fields-supported?)))

(re-frame/reg-sub
  ::test-case-versioning-enabled?
  :<- [::options]
  (fn [{:keys [test-case-versioning-enabled?]}]
    (boolean test-case-versioning-enabled?)))

(re-frame/reg-sub
  ::versioning-can-use-labels?
  :<- [::options]
  (fn [{:keys [versioning-can-use-labels?]}]
    (boolean versioning-can-use-labels?)))

(re-frame/reg-sub
  ::versioning-can-create-labels?
  :<- [::options]
  (fn [{:keys [versioning-can-create-labels?]}]
    (boolean versioning-can-create-labels?)))

(re-frame/reg-sub
  ::history-filter
  :<- [:practitest.frontend.common-entities.events/history-filter]
  (fn [filter _]
    filter))

(def readiness-index-allowed-to #{"Corporate" "Team"})

(defn milestone-readiness-index-enabled? [db]
  (and (boolean (get-in db [:options :milestone-readiness-index-enabled?]))
       (boolean (readiness-index-allowed-to (get-in db [:current-account :license])))))

(re-frame/reg-sub
 ::milestone-sprint-sync-enabled?
 :<- [::options]
 (fn [{:keys [milestone-sprint-sync-enabled?]}]
   (boolean milestone-sprint-sync-enabled?)))

(re-frame/reg-sub
 ::milestone-readiness-index-enabled?
 (fn [db _]
   (milestone-readiness-index-enabled? db)))

